import Immutable, { OrderedSet } from 'immutable';
import type Luminary from '@lumen5/luminary';
import type {
    Asset,
    AssetGroup,
    AudioAsset,
    AudioSourceData,
    CaptionAsset,
    ColorAsset,
    FancyCaptionAsset,
    GradientAsset,
    IconAsset,
    IconSourceData,
    LottieAsset,
    MediaSourceData,
    RectangleAsset,
    Scene,
    SceneCategory,
    SceneGroup,
    Subscene,
    TextAsset,
    TimeAwareMediaAsset,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type {
    AudioAssetModel,
    GenerateIconAssetMetadata,
    GenerateMediaAssetMetadata,
    GenerateSceneMetadata,
    SlideBackgroundModelTypeEnum,
} from 'js/src/client';
import { AssetContainerTypes, NarrativeThreadEnum, SourceEnum } from 'js/src/client';
import { Placement } from 'js/src/components/common/ui/Tooltip';
import type { FlattenedAsset } from 'js/src/data/media';
import { flattenedAssetIsVideo, isAudioAsset, isAudioSourceData } from 'js/src/data/media';
import type { SceneDesignId } from 'js/src/data/templates';
import {
    getTemplateDefaultTalkingHeadSceneDesign,
    getTemplateDefaultVoiceoverSceneDesign,
} from 'js/src/data/templates';
import { AudioVolume, FREE_PLAN_NAME } from 'js/src/libs/consts';
import PremiumMediaChecker, { PREMIUM_LEVEL } from 'js/src/libs/premium-media-checker';
import type { RootState } from 'js/src/libs/RootState';
import {
    getLinkedMediaAssets,
    TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA,
    VOICEOVER_PLACEHOLDER_ASSET_SOURCE,
} from 'js/src/libs/scene-utils';
import { RENDERING_BLOCKING_SOURCES } from 'js/src/libs/video-data-consts';
import { equalWithinEpsilon } from 'js/src/luminary/comparison-utils';
import { getEditableMediaAssets } from 'js/src/luminary/index';
import {
    findScreenRecordingMediaAssetsInScene,
    hasScreenRecordingSuffix,
    isVoiceDrivenCompatibleSceneCategory,
} from 'js/src/luminary/luminary-utils';

/**
 *
 * Returns the list of narrative threads used in a video
 */
export function getNarrativeThreads(
    luminary: Luminary | Readonly<Luminary>,
): NarrativeThreadEnum[] {
    return OrderedSet(
        luminary.sceneGroups.map((sceneGroup) => {
            if (isTalkingHeadSceneGroup(sceneGroup) || isAIAvatarSceneGroup(sceneGroup)) {
                return NarrativeThreadEnum.TALKING_HEAD;
            } else if (isVoiceoverSceneGroup(sceneGroup)) {
                return NarrativeThreadEnum.VOICEOVER;
            } else {
                return NarrativeThreadEnum.TEXT;
            }
        }),
    ).toArray();
}

/**
 * Returns a list of the scene numbers with marketplace media
 */
export function getSceneNumbersWithPremiumMedia(luminary: Luminary): number[] {
    return luminary.scenes
        .filter((scene) => {
            const mediaAssets = getEditableMediaAssets(scene);
            return mediaAssets.some((mediaAsset) =>
                RENDERING_BLOCKING_SOURCES.includes(mediaAsset.sourceData.source),
            );
        })
        .map((scene) => scene.number);
}

export function getPremiumMediaByScene(
    luminary: Luminary,
): Array<{ sceneNumber: number; thumbnailUrls: string[] }> {
    return luminary.scenes
        .filter((scene) => {
            const mediaAssets = getEditableMediaAssets(scene);
            return mediaAssets.some((mediaAsset) =>
                RENDERING_BLOCKING_SOURCES.includes(mediaAsset.sourceData.source),
            );
        })
        .map((scene) => ({
            sceneNumber: scene.number,
            thumbnailUrls: getEditableMediaAssets(scene)
                .filter((mediaAsset) =>
                    RENDERING_BLOCKING_SOURCES.includes(mediaAsset.sourceData.source),
                )
                .map((mediaAsset) =>
                    mediaAsset.isVideo ? mediaAsset.posterUrl : mediaAsset.thumbnailUrl,
                )
                .filter((url): url is string => url != null),
        }));
}

/**
 * @returns {Object} - A list in the form:
 *   [
 *       {
 *           'sceneId': <sceneId>,
 *           'level': '<PREMIUM_LEVEL enum>',
 *           'sourceData': MediaAsset.sourceData,
 *      }, ...
 *   ]
 *
 * Since this returns a non-immutable list we shouldn't
 * pass it in directly to a React Component's props. Instead, other
 * selectors should call this and then do a little more calculation
 * to return some conclusion to pass into a React Component's props.
 */
function _getPremiumMediaFiles(luminary: Luminary) {
    const returnData: {
        sceneId: string;
        level: PREMIUM_LEVEL;
        sourceData: MediaSourceData;
    }[] = [];
    luminary.scenes.forEach((slide) => {
        const mediaAssets = getEditableMediaAssets(slide);
        mediaAssets.forEach((mediaAsset) => {
            const mediaSource = mediaAsset.sourceData.source;
            const level = PremiumMediaChecker.badge_required_for_source(
                mediaSource,
                AssetContainerTypes.BACKGROUND,
            );
            if (
                level === PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE ||
                level === PREMIUM_LEVEL.UPGRADE_FOR_UNLIMITED ||
                level === PREMIUM_LEVEL.MARKETPLACE
            ) {
                returnData.push({
                    sceneId: slide.id,
                    level,
                    sourceData: mediaAsset.sourceData,
                });
            }
        });
    });
    return returnData;
}

/**
 * @returns {string|null} - one of, in this order of precedence:
 *   - PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE if there are any pieces of marketplace media and the user must upgrade to access marketplace media
 *   - PREMIUM_LEVEL.UPGRADE_FOR_UNLIMITED if there are unlimited-premium media files (and no marketplace media
 *   - PREMIUM_LEVEL.MARKETPLACE if there are marketplace media files in the video (and no unlimited-premium, and the user doesn't need to upgrade)
 *   - null if there are no media files to upgrade for
 */
export function userMustPayForMediaToLevel(luminary: Luminary): PREMIUM_LEVEL | null {
    let highestLevel = null;
    const premiumMediaFiles = _getPremiumMediaFiles(luminary);
    for (const mediaFileInfo of premiumMediaFiles) {
        const mediaLevel = mediaFileInfo.level;
        if (mediaLevel === PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE) {
            highestLevel = PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE;
        } else if (
            mediaLevel === PREMIUM_LEVEL.UPGRADE_FOR_UNLIMITED &&
            highestLevel !== PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE
        ) {
            highestLevel = PREMIUM_LEVEL.UPGRADE_FOR_UNLIMITED;
        } else if (
            mediaLevel === PREMIUM_LEVEL.MARKETPLACE &&
            highestLevel !== PREMIUM_LEVEL.UPGRADE_FOR_MARKETPLACE &&
            highestLevel !== PREMIUM_LEVEL.UPGRADE_FOR_UNLIMITED
        ) {
            highestLevel = PREMIUM_LEVEL.MARKETPLACE;
        }
    }
    return highestLevel;
}

/**
 * Returns the properties to render an ImageAsset or VideoAsset for the first media asset that is blocking rendering.
 */
export function getSourceDataForFirstMediaAssetToPayFor(
    luminary: Luminary,
): MediaSourceData | IconSourceData | null {
    const premiumMediaFiles = _getPremiumMediaFiles(luminary);
    if (premiumMediaFiles.length === 0) {
        return null;
    }
    const mediaFileInfo = premiumMediaFiles[0];
    return mediaFileInfo.sourceData;
}

export function hasEditorialMedia(luminary: Luminary): boolean {
    return luminary.scenes.some((scene) => {
        return getEditableMediaAssets(scene).some((asset) => asset.isEditorial);
    });
}

export function isVideoL5Branded(luminary: Luminary): boolean {
    return luminary.scenes.some((scene) => {
        return scene.sceneCategory === VideoData.CATEGORY_BRAND;
    });
}

export function sceneGroupDurationsWithoutTransitions(luminary: Luminary): number[] {
    return luminary.sceneGroups.map((sceneGroup) => {
        const numScenes = sceneGroup.scenes.length;
        return (
            sceneGroup.duration -
            sceneGroup.scenes[numScenes - 1].customTransition.transitionDuration
        );
    });
}

export function sceneGroupStartTimes(luminary: Luminary): number[] {
    return luminary.sceneGroups.map((sceneGroup) => {
        return sceneGroup.startTime;
    });
}

export function slideStartTimes(luminary: Luminary): number[] {
    return luminary.scenes.map((slide) => {
        return slide.startTime;
    });
}

export function slideEndTimes(luminary: Luminary): number[] {
    return luminary.scenes.map((slide) => {
        return slide.endTime;
    });
}

/**
 * Returns an Immutable.Map dictionary of sceneIds based on startTimes
 * Useful for searching for specific sceneId for scene-related data where
 * the index is time-based
 * ie. searching up comments for a specific scene when clicking through <SlideMarkers/>
 *     in a timeline (indexed based on startTime)
 * @returns {Immutable.Map({ startTime<number>: sceneId<string> })}
 */
export function slideIdsByStartTime(luminary: Luminary): Immutable.Map<number, string> {
    return luminary.scenes.reduce((idsByStartTime, scene) => {
        return idsByStartTime.set(scene.startTime, scene.id);
    }, Immutable.Map()) as Immutable.Map<number, string>;
}

/**
 * Returns all of the TextAssets on a given subscene.
 */
export function getEditableSubsceneTextAssetArray(subscene: Subscene): TextAsset[] {
    if (!subscene) {
        return [];
    }
    return subscene.getAssetsByType(VideoData.TEXT_ASSET_TYPE).filter((child) => child.isEditable);
}

/**
 * Returns the top level asset group containing the provided asset, if any
 */
export function getRootAssetGroup(asset: Asset): AssetGroup | undefined {
    let currGroup = asset.getParentByType(VideoData.GROUP_ASSET_TYPE);
    while (currGroup?.getParentByType(VideoData.GROUP_ASSET_TYPE)) {
        currGroup = currGroup.getParentByType(VideoData.GROUP_ASSET_TYPE);
    }
    return currGroup;
}

/**
 * Returns all of the TextAssets on a given scene.
 */
export function getEditableSceneTextAssetArray(scene: Scene): TextAsset[] {
    if (!scene) {
        return [];
    }
    return scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE).filter((child) => child.isEditable);
}

/**
 * Returns all TextAsset children of a group that are editable
 */
export function getEditableTextAssetsFromGroup(group: AssetGroup): TextAsset[] {
    if (!group) {
        return [];
    }
    return group.getAssetsByType(VideoData.TEXT_ASSET_TYPE).filter((child) => child.isEditable);
}

/**
 * Returns all of the texts on a given subscene.
 */
export function getEditableSubslideTextArray(subscene: Subscene): string[] {
    return getEditableSubsceneTextAssetArray(subscene)
        .filter((fe) => fe.text)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((fe) => fe.text);
}

/**
 * Returns all of the text on a given subscene. Multiple text elements are joined together (space separated).
 */
export function getEditableSubslideText(subscene: Subscene): string {
    return getEditableSubslideTextArray(subscene).join(' ');
}

/**
 * Returns all of the texts on a given scene.
 */
export function getEditableSlideTextArray(scene: Scene): string[] {
    return scene.subscenes.flatMap(getEditableSubslideTextArray);
}

/**
 * Returns all of the text on a given scene. Multiple text elements & subscenes are joined together (space separated).
 */
export function getEditableSceneText(scene: Scene): string {
    return getEditableSlideTextArray(scene).join(' ');
}

/**
 * Calculates if an AssetGroup has any padding, given to it from a graphic style, or if the topmost/bottommost
 * asset is an outro media asset. If it does, and it contains a text asset that needs to render the Plus button
 * flush to the group bounds, this method will return data needed to render the Plus button relative to the
 * asset group instead.
 */
export function getAddButtonForAssetGroup(assetGroup: AssetGroup): {
    textAssetId: string; // id of the asset this button is originally meant for
    position: typeof Placement.TOP | typeof Placement.BOTTOM; // one of 'top' or 'bottom')
    targetAssetId: string; // id of adjacent empty assset
} | null {
    // determine top and bottom non-empty children in group
    const childrenList = assetGroup
        .getAssets()
        .filter(
            (asset) =>
                VideoData.isTextAsset(asset) ||
                VideoData.isMediaAsset(asset) ||
                VideoData.isIconAsset(asset) ||
                VideoData.isFancyCaptionAsset(asset),
        )
        .filter((asset) => asset.isEditable);

    const isMediaOrIconOrFancyCaptionAsset = (asset: Asset) =>
        VideoData.isMediaAsset(asset) ||
        VideoData.isIconAsset(asset) ||
        VideoData.isFancyCaptionAsset(asset);
    let topAssetIdx: number | undefined;
    let bottomAssetIdx: number | undefined;
    childrenList.forEach((asset, i: number) => {
        const isNonEmptyTextAsset = VideoData.isTextAsset(asset) && asset.text.length > 0;
        const isNonEmpty = isMediaOrIconOrFancyCaptionAsset(asset) || isNonEmptyTextAsset;
        if (isNonEmpty) {
            if (topAssetIdx === null) {
                topAssetIdx = i;
            }
            bottomAssetIdx = i;
        }
    });

    if (topAssetIdx === undefined || bottomAssetIdx === undefined) {
        return null;
    }

    // determine if there is any top or bottom padding associated with the asset group, or if the top/bottom
    // asset is an outro media asset
    const assetBounds = assetGroup.boundsWithoutAnimation;
    const hasTopPadding = assetBounds.y < childrenList[topAssetIdx].boundsWithoutAnimation.y;

    const bottomTextAssetBounds = childrenList[bottomAssetIdx].boundsWithoutAnimation;
    const bottomGroupYCoord = assetBounds.y + assetBounds.height;
    const bottomTextYCoord = bottomTextAssetBounds.y + bottomTextAssetBounds.height;
    const hasBottomPadding = bottomGroupYCoord > bottomTextYCoord;

    // determine if the top or bottom most non empty asset is actually a media asset
    const isTopAssetMedia = isMediaOrIconOrFancyCaptionAsset(childrenList[topAssetIdx]);
    const isBottomAssetMedia = isMediaOrIconOrFancyCaptionAsset(childrenList[bottomAssetIdx]);

    if (!hasTopPadding && !hasBottomPadding && !isTopAssetMedia && !isBottomAssetMedia) {
        return null;
    }

    // determine which button should be attached to the asset group and its target
    if (topAssetIdx !== 0) {
        return {
            textAssetId: childrenList[topAssetIdx].id,
            targetAssetId: childrenList[topAssetIdx - 1].id,
            position: Placement.TOP,
        };
    } else if (bottomAssetIdx !== childrenList.length - 1) {
        return {
            textAssetId: childrenList[bottomAssetIdx].id,
            targetAssetId: childrenList[bottomAssetIdx + 1].id,
            position: Placement.BOTTOM,
        };
    } else {
        return null;
    }
}

type AssetContainingText = TextAsset | FancyCaptionAsset;

/**
 * For Natural Text Editing -- determines which non-empty text assets in an asset group
 * should have a "Add New Text" button attached to it.
 */
export function getTextAssetsToRenderAddButton(assetGroup: AssetGroup): {
    [key: string]: {
        // textAsset.id
        position: typeof Placement.TOP | typeof Placement.BOTTOM; // one of 'top' or 'bottom')
        targetAssetId: string; // id of adjacent empty asset
    }[];
} {
    // get editable assets, which should be ordered "top to bottom" in terms
    // of how they are vertically stacked
    const textAssets = assetGroup
        .getAssets()
        .filter(
            (asset) =>
                (VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset)) &&
                asset.isEditable,
        ) as AssetContainingText[];

    // determine if any text asset is adjacent to any empty asset
    const result: Record<
        string,
        { position: typeof Placement.TOP | typeof Placement.BOTTOM; targetAssetId: string }[]
    > = {};
    const targetAssetIdSet = new Set();
    const endInd = textAssets.length - 1;

    for (let i = endInd; i >= 0; i--) {
        const currAsset = textAssets[i];
        // skip text assets that are empty
        if (VideoData.isTextAsset(currAsset) && currAsset.text.length === 0) {
            continue;
        }

        const adjacentAssets: {
            position: typeof Placement.TOP | typeof Placement.BOTTOM;
            targetAssetId: string;
        }[] = [];
        const isEmptyAndNonEditing = (textAsset: AssetContainingText): boolean => {
            if (VideoData.isFancyCaptionAsset(textAsset)) {
                return false;
            }
            return textAsset.text.length === 0 && !textAsset.hasTextEditingEnabled;
        };

        // determine any adjacent bottom/top adjacent elements
        if (i > 0) {
            const topAdjacentAsset = textAssets[i - 1];
            if (isEmptyAndNonEditing(topAdjacentAsset)) {
                adjacentAssets.push({
                    targetAssetId: topAdjacentAsset.id,
                    position: Placement.TOP,
                });
                targetAssetIdSet.add(topAdjacentAsset.id);
            }
        }
        if (i < endInd) {
            const bottomAdjacentAsset = textAssets[i + 1];
            const isAlreadyAdded = targetAssetIdSet.has(bottomAdjacentAsset.id);
            const isNotActive = isEmptyAndNonEditing(bottomAdjacentAsset);

            if (!isAlreadyAdded && isNotActive) {
                adjacentAssets.push({
                    targetAssetId: bottomAdjacentAsset.id,
                    position: Placement.BOTTOM,
                });
                targetAssetIdSet.add(bottomAdjacentAsset.id);
            }
        }

        if (adjacentAssets.length) {
            result[currAsset.id] = adjacentAssets;
        }
    }
    return result;
}

export function getInvisibleAssetNames(luminary: Luminary, sceneId: string): string[] {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) {
        return [];
    }
    const sceneChildren = scene.getAssets();
    let colorAssetName = null;
    let colorHeight = 0;
    let colorWidth = 0;
    const invisibleAssetNames: string[] = [];

    // the base color asset is invisible if an editable media asset is on top of it and that media asset is at least as big
    // as the color asset
    for (let i = 0; i < sceneChildren.length; i++) {
        const child = sceneChildren[i];

        if (VideoData.isColorAsset(child)) {
            colorAssetName = child.name;
            colorHeight = sceneChildren[i].height;
            colorWidth = sceneChildren[i].width;
        } else if (VideoData.isMediaAsset(child)) {
            const childWidth = equalWithinEpsilon(child.width, 1)
                ? child.approximateMaxBounds.width
                : child.width;
            const childHeight = equalWithinEpsilon(child.height, 1)
                ? child.approximateMaxBounds.height
                : child.height;
            const blocksBackground =
                child.thumbnailUrl !== VideoData.INVISIBLE_IMAGE &&
                child.isEditable &&
                childHeight >= colorHeight &&
                childWidth >= colorWidth;
            if (colorAssetName && blocksBackground) {
                invisibleAssetNames.push(colorAssetName);
                break;
            }
        }
    }

    // also, text assets are invisible if there's no text inside of them
    // text assets can be at the scene level or subscene level. If they are
    // on the subscene, there will be multiple text assets with the same name.
    // This asset is only invisible if all the text assets with the same name
    // are empty.
    const allTextAssets = scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE);
    const textAssetNames = new Set(allTextAssets.map((textAsset) => textAsset.name));
    textAssetNames.forEach((textAssetName) => {
        const textAssets = allTextAssets.filter((textAsset) => textAsset.name === textAssetName);
        const allEmpty = textAssets.every((textAsset) => textAsset.text.length === 0);
        if (allEmpty) {
            invisibleAssetNames.push(textAssetName);
        }
    });

    // groups are invisible if all of their children are invisible
    const allGroups = scene.getAssetsByType(VideoData.GROUP_ASSET_TYPE);
    allGroups.forEach((group) => {
        const allChildrenInvisible = group
            .getAssets()
            .every((child) => invisibleAssetNames.includes(child.name));
        if (allChildrenInvisible) {
            invisibleAssetNames.push(group.name);
        }
    });

    return invisibleAssetNames;
}

/**
 * If text editing is currently enabled, returns the root HTML element of the text editing input
 */
export function getTextEditingContainer(): HTMLElement | null {
    return document.querySelector('#luminary-nte-container');
}

export type ColorGraphicsMap = {
    [assetId: string]: {
        [attributePath: string]: number; // attribute path (eg. "colorGraphics[0].shape.fillColor")
    };
};

/**
 * Get all colorGraphics color related attributes of a given asset and its parent (if it exists).
 * Returns an object with the color value and assetId where the attribute is the key
 */
function getColorGraphicsForAssetAndParents(
    asset:
        | TextAsset
        | TimeAwareMediaAsset
        | IconAsset
        | AssetGroup
        | FancyCaptionAsset
        | CaptionAsset,
): ColorGraphicsMap {
    const accentColorMap = {
        [asset.id]: {
            ...asset.buildColorGraphicsMap(),
        },
    };
    const assetGroup = asset.getParentByType(VideoData.GROUP_ASSET_TYPE);
    if (assetGroup) {
        return {
            ...accentColorMap,
            ...getColorGraphicsForAssetAndParents(assetGroup),
        };
    }
    return accentColorMap;
}

export type AssetColor = {
    assetId: string;
    path: string;
    index: number;
    color: number;
    alphaPath?: string;
    alpha?: number;
};

export function getAccentColorsList(
    asset:
        | TextAsset
        | TimeAwareMediaAsset
        | IconAsset
        | AssetGroup
        | FancyCaptionAsset
        | CaptionAsset,
): AssetColor[] {
    const accentColorGraphicMap = getColorGraphicsForAssetAndParents(asset);
    const accentColors: AssetColor[] = [];
    let accentIndex = 0;
    Object.entries(accentColorGraphicMap).forEach(([assetId, colorGraphicMap]) => {
        Object.entries(colorGraphicMap).forEach(([accentColorPath, accentColorGraphic]) => {
            accentColors.push({
                assetId,
                path: accentColorPath,
                index: accentIndex,
                color: accentColorGraphic,
            });
            accentIndex++;
        });
    });

    return accentColors;
}

export type BackgroundColorData = {
    assetColors: AssetColor[];
};

function getColorPropertyPath(
    colorAsset: ColorAsset | RectangleAsset | LottieAsset | GradientAsset,
): string | string[] {
    if (VideoData.isColorAsset(colorAsset)) {
        return 'sourceColor';
    }
    if (VideoData.isRectangleAsset(colorAsset)) {
        return 'color';
    }
    if (VideoData.isLottieAsset(colorAsset)) {
        const nonTransparentColorMaps = colorAsset.getNonTransparentColorMap();
        return nonTransparentColorMaps.map((colorMap) => {
            const index = colorAsset.colorMaps.indexOf(colorMap);
            return `colorMaps[${index}].targetColor`;
        });
    }
    if (VideoData.isGradientAsset(colorAsset)) {
        return colorAsset.colorStops.map((_colorStop, index) => {
            return `colorStops.${index}.color`;
        });
    }

    throw new Error('Invalid asset type');
}

function getAlphaPropertyPath(asset: ColorAsset | RectangleAsset): string {
    if (VideoData.isColorAsset(asset)) {
        return 'sourceAlpha';
    }
    if (VideoData.isRectangleAsset(asset)) {
        return 'fillAlpha';
    }
    throw new Error('Invalid asset type');
}
function getAlphaProperty(asset: ColorAsset | RectangleAsset): number {
    if (VideoData.isColorAsset(asset)) {
        return asset.sourceAlpha;
    }
    if (VideoData.isRectangleAsset(asset)) {
        return asset.fillAlpha;
    }
    throw new Error('Invalid asset type');
}

function getColorProperty(
    colorAsset: ColorAsset | RectangleAsset | LottieAsset | GradientAsset,
    colorIndex = 0,
): number {
    if (VideoData.isColorAsset(colorAsset)) {
        return colorAsset.sourceColor;
    }

    if (VideoData.isLottieAsset(colorAsset)) {
        const nonTransparentColorMaps = colorAsset.getNonTransparentColorMap();
        if (!nonTransparentColorMaps || colorIndex >= nonTransparentColorMaps.length) {
            throw new Error('Invalid colorIndex for LottieAsset.colorMaps.');
        }
        return nonTransparentColorMaps[colorIndex].targetColor;
    }

    if (VideoData.isRectangleAsset(colorAsset)) {
        return colorAsset.color;
    }

    if (VideoData.isGradientAsset(colorAsset)) {
        if (colorIndex >= colorAsset.colorStops.length) {
            throw new Error('Invalid colorIndex for GradientAsset.colorStops.');
        }
        return colorAsset.colorStops[colorIndex].color;
    }

    throw new Error('Invalid asset type');
}

/**
 * Gets background color data for a scene including asset colors and alpha information.
 * Returns primitive values only (no mutable luminary objects).
 */
export function getBackgroundColorData(scene: Readonly<Scene>): BackgroundColorData | null {
    const colorAssets = scene
        ?.getAssets()
        .filter((asset): asset is ColorAsset | RectangleAsset | LottieAsset | GradientAsset => {
            return Boolean(
                VideoData.isColorAsset(asset) ||
                VideoData.isRectangleAsset(asset) ||
                (VideoData.isLottieAsset(asset) && asset.colorMaps?.length) ||
                VideoData.isGradientAsset(asset),
            );
        });

    const colorEditableAssets =
        colorAssets.filter((asset) => {
            if (!asset.isColorEditable) {
                return false;
            }
            if (!VideoData.isLottieAsset(asset)) {
                return true;
            }
            return asset.getNonTransparentColorMap().length;
        }) || [];

    if (colorEditableAssets.length === 0) {
        return null;
    }

    const assetColors = colorEditableAssets
        .flatMap((asset) => {
            const pathOrPathArray = getColorPropertyPath(asset);
            const supportsAlpha =
                VideoData.isColorAsset(asset) || VideoData.isRectangleAsset(asset);

            if (!Array.isArray(pathOrPathArray)) {
                return {
                    assetId: asset.id,
                    path: pathOrPathArray,
                    index: 0,
                    color: getColorProperty(asset),
                    ...(supportsAlpha && {
                        alphaPath: getAlphaPropertyPath(asset),
                        alpha: getAlphaProperty(asset),
                    }),
                };
            }

            return pathOrPathArray.map((path, i) => {
                return {
                    assetId: asset.id,
                    path: path,
                    index: 0,
                    color: getColorProperty(asset, i),
                    ...(supportsAlpha && {
                        alphaPath: getAlphaPropertyPath(asset),
                        alpha: getAlphaProperty(asset),
                    }),
                };
            });
        })
        .map((asset, index) => ({ ...asset, index }));

    return {
        assetColors,
    };
}

export function isTalkingHeadSceneGroup(sceneGroup: Readonly<SceneGroup> | null | undefined) {
    return !!sceneGroup?.mediaAsset;
}

export function hasScreenRecording(sceneGroup: SceneGroup | null | undefined) {
    if (!sceneGroup) {
        return false;
    }
    const sourceData = getVoiceDrivenAssetSourceData(sceneGroup);
    if (!sourceData) {
        return false;
    }
    const sourceId = sourceData.source_id;
    if (!sourceId) {
        return false;
    }
    return hasScreenRecordingSuffix(sourceId);
}

export function isAIAvatarSceneGroup(sceneGroup: Readonly<SceneGroup> | null | undefined) {
    return !!sceneGroup?.avatar?.foreground;
}

export function isVoiceoverSceneGroup(sceneGroup: Readonly<SceneGroup> | null | undefined) {
    return !!sceneGroup?.voiceoverAsset;
}

export function isAIVoiceoverSceneGroup(sceneGroup: SceneGroup) {
    return (
        (sceneGroup?.voiceoverAsset?.sourceData?.source as SourceEnum) ===
        SourceEnum.SOURCE_AI_VOICEOVER
    );
}

export function isHumanVoicedrivenSceneGroup(sceneGroup: SceneGroup) {
    return (
        (isVoiceoverSceneGroup(sceneGroup) && !isAIVoiceoverSceneGroup(sceneGroup)) ||
        isTalkingHeadSceneGroup(sceneGroup)
    );
}

export function isTextCorrectionLockedByUpsell(sceneGroup: SceneGroup) {
    const planName = SERVER.ANALYTICS?.plan_name || FREE_PLAN_NAME;
    return isAIVoiceoverSceneGroup(sceneGroup) && planName === FREE_PLAN_NAME;
}

export function canSceneGroupUseEditAIVO(sceneGroup: SceneGroup) {
    const planName = SERVER.ANALYTICS?.plan_name || FREE_PLAN_NAME;
    return (
        isAIVoiceoverSceneGroup(sceneGroup) &&
        !!sceneGroup.voiceoverAsset.sourceData.ai_voice && // the ai voice needs to be set in the source data. Old AI VO scenegroups won't have this
        planName !== FREE_PLAN_NAME
    );
}

/**
 * Both talking head and voiceover scene groups are controlled via transcript and
 * scenes are added as overlays.
 */
export function isVoiceDrivenSceneGroup(sceneGroup: Readonly<SceneGroup>) {
    return isTalkingHeadSceneGroup(sceneGroup) || isVoiceoverSceneGroup(sceneGroup);
}

/**
 * Returns the first talking head or voiceover scene group
 * @param luminary
 * @returns SceneGroup
 */
export function getFirstVoiceDrivenSceneGroup(luminary: Readonly<Luminary>) {
    const firstVoiceDrivenSceneGroup = luminary.sceneGroups.find((sceneGroup: SceneGroup) => {
        return (
            isVoiceDrivenSceneGroup(sceneGroup) &&
            !isVoiceDrivenSceneUsingPlaceholderBaseAsset(sceneGroup)
        );
    });

    return firstVoiceDrivenSceneGroup;
}

/**
 * Given a voicedriven scene group, return the corresponding asset.
 * Assumption: Scene Group cannot have both mediaAsset and voiceoverAsset (mutually exclusive)
 */
export function getVoiceDrivenAsset(
    sceneGroup: SceneGroup,
): null | TimeAwareMediaAsset | AudioAsset {
    if (!sceneGroup) {
        return null;
    }

    if (!isVoiceDrivenSceneGroup(sceneGroup)) {
        return null;
    }

    return sceneGroup.mediaAsset || sceneGroup.voiceoverAsset;
}

export function getVoiceDrivenAssetSourceData(
    sceneGroup: SceneGroup,
): MediaSourceData | AudioSourceData | undefined {
    const asset = getVoiceDrivenAsset(sceneGroup);
    return asset?.sourceData;
}

export function getVoiceDrivenAssetSource(sceneGroup: SceneGroup): number | undefined {
    const sourceData = getVoiceDrivenAssetSourceData(sceneGroup);
    return sourceData?.source;
}

export function getVoiceDrivenAssetTitle(sceneGroup: SceneGroup): string | null {
    const sourceData = getVoiceDrivenAssetSourceData(sceneGroup);
    return sourceData?.title ?? null;
}

export function getVoiceDrivenAssetAIVoice(sceneGroup: SceneGroup): string | null {
    const sourceData = getVoiceDrivenAssetSourceData(sceneGroup);
    if (!sourceData || !isAudioSourceData(sourceData)) {
        return null;
    }
    return sourceData.ai_voice || null;
}

export const isSceneGroupAssetAudioAsset = (
    sceneGroupAsset: TimeAwareMediaAsset | AudioAsset,
): sceneGroupAsset is AudioAsset => {
    return !!(sceneGroupAsset as AudioAsset).sourceData?.mp3_url;
};

export const isSceneGroupSourceAudioEnhanced = (sceneGroup: SceneGroup): boolean => {
    const source = getVoiceDrivenAssetSource(sceneGroup);
    return source === SourceEnum.SOURCE_AUDIO_ENHANCEMENT;
};

/**
 * Given a scene design id, determine if it's an overlay design for voicedriven scenes
 *
 * @param sceneCategory - checks if the sceneCategory is voice-driven (talking head || voiceover)
 * @param sceneDesignId - checks if the sceneDesignId is the base design ids
 * @param templateDefaults - caller needs to supply the base design ids
 * @returns boolean - whether a sceneDesignId is an overlay scene design for voicedriven
 */
export function isOverlaySceneDesign(
    sceneCategory: SceneCategory,
    sceneDesignId: string,
    {
        defaultTalkingHeadSceneDesignId,
        defaultVoiceoverSceneDesignId,
    }: {
        defaultTalkingHeadSceneDesignId: string | null;
        defaultVoiceoverSceneDesignId: string | null;
    },
) {
    // in the future when we want to show ordinary scene designs as overlays we'll remove this category restriction
    // currently some underlying work needs to be done (ie. managing scene transitions)
    if (isVoiceDrivenCompatibleSceneCategory(sceneCategory)) {
        if (
            sceneDesignId !== defaultTalkingHeadSceneDesignId &&
            sceneDesignId !== defaultVoiceoverSceneDesignId
        ) {
            return true;
        }
    }

    return false;
}

export function isOrdinarySceneGroup(sceneGroup: SceneGroup) {
    return getSceneGroupCategory(sceneGroup) === VideoData.CATEGORY_ORDINARY;
}

export function isOutroSceneGroup(sceneGroup: SceneGroup) {
    return getSceneGroupCategory(sceneGroup) === VideoData.CATEGORY_OUTRO;
}

/*
 * Get the inferred "category" of a Scene Group
 */
export function getSceneGroupCategory(sceneGroup: SceneGroup) {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        return VideoData.CATEGORY_TALKING_HEAD;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        return VideoData.CATEGORY_VOICEOVER;
    } else {
        // we are not dealing with a voice driven scene group. In this case, we can use the
        // first scene's category as the scene group's category.
        const sceneCategories = sceneGroup.scenes.map((scene) => scene.sceneCategory);

        // Common and Ordinary scene designs can be used on ordinary scene groups. Simplify this by
        // returning ORDINARY for both.
        const ordinaryCategories: SceneCategory[] = [
            VideoData.CATEGORY_COMMON,
            VideoData.CATEGORY_ORDINARY,
        ];
        if (ordinaryCategories.includes(sceneCategories[0])) {
            return VideoData.CATEGORY_ORDINARY;
        }

        // we are not dealing with a voicedriven scenegroup, or an ordinary scenegroup.
        // In this case, just use the first scene's category.
        return sceneCategories[0];
    }
}

export function isSceneAnOverlay(state: RootState, scene: Scene): boolean {
    const templateId = scene.template.id;
    const defaultTalkingHeadSceneDesignId = getTemplateDefaultTalkingHeadSceneDesign(
        state,
        templateId,
    );
    const defaultVoiceoverSceneDesignId = getTemplateDefaultVoiceoverSceneDesign(state, templateId);
    return isOverlaySceneDesign(scene.sceneCategory, scene.sceneDesign, {
        defaultTalkingHeadSceneDesignId,
        defaultVoiceoverSceneDesignId,
    });
}

export function getOverlayScenesInSceneGroup(state: RootState, sceneGroup: SceneGroup): Scene[] {
    if (!sceneGroup?.scenes?.[0]) {
        return [];
    }
    const templateId = sceneGroup.scenes[0].template.id;
    const defaultTalkingHeadSceneDesignId = getTemplateDefaultTalkingHeadSceneDesign(
        state,
        templateId,
    );
    const defaultVoiceoverSceneDesignId = getTemplateDefaultVoiceoverSceneDesign(state, templateId);
    return sceneGroup.scenes.filter((scene: Scene) => {
        return isOverlaySceneDesign(scene.sceneCategory, scene.sceneDesign, {
            defaultTalkingHeadSceneDesignId,
            defaultVoiceoverSceneDesignId,
        });
    });
}

export function getBaseSceneDesignId(
    state: RootState,
    sceneGroup: SceneGroup,
): SceneDesignId | null {
    const templateId = sceneGroup.root.template.id;
    if (isTalkingHeadSceneGroup(sceneGroup) || isAIAvatarSceneGroup(sceneGroup)) {
        return getTemplateDefaultTalkingHeadSceneDesign(state, templateId);
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        return getTemplateDefaultVoiceoverSceneDesign(state, templateId);
    }
    return null;
}

export function getSceneWithBaseMediaAsset(sceneGroup: SceneGroup) {
    return sceneGroup.scenes.find((scene: Scene) => {
        return getLinkedMediaAssets(scene).length > 0;
    });
}

/*
 * Retrieve the endTime for the base asset of a voicedriven scene group
 */
export function getVoiceDrivenBaseAssetEndTime(sceneGroup: SceneGroup): number {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        const endTime = sceneGroup.mediaAsset.endTime as number;
        return endTime;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        return sceneGroup.voiceoverAsset.endTime;
    }
    return 0;
}

/*
 * Retrieve the startTime for the base asset of a voicedriven scene group
 */
export function getVoiceDrivenBaseAssetStartTime(sceneGroup: SceneGroup): number {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        return sceneGroup.mediaAsset.startTime;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        return sceneGroup.voiceoverAsset.startTime;
    }
    return 0;
}

export function getVoiceDrivenBaseAssetCutRegions(sceneGroup: SceneGroup): Array<[number, number]> {
    return VideoData.getCutRegions(
        getVoiceDrivenAssetPlaybackRegions(sceneGroup),
        getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
    );
}

export type PlaybackRegions = ReturnType<typeof getVoiceDrivenAssetPlaybackRegions>;
export function getVoiceDrivenAssetPlaybackRegions(sceneGroup: SceneGroup) {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        return sceneGroup.mediaAsset.playbackRegions;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        return sceneGroup.voiceoverAsset.playbackRegions;
    }
    return [];
}

/*
 * Retrieve the duration for the base asset of a voicedriven scene group in milliseconds
 */
export function getVoiceDrivenBaseAssetSourceDuration(sceneGroup: SceneGroup): number {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        const sourceDuration = sceneGroup.mediaAsset.sourceDuration as number;
        return sourceDuration;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        return sceneGroup.voiceoverAsset.sourceDuration;
    }
    return 0;
}

export function getVoiceDrivenAssetDurationAfterCutsApplied(sceneGroup: SceneGroup): number {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        return sceneGroup.mediaAsset.duration;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        return sceneGroup.voiceoverAsset.duration;
    }
    throw new Error('Cannot get the duration after cuts of a non-voicedriven scene group');
}

/*
 * Retrieve the volume for the base asset of a voicedriven scene group (0 to 100).
 */
export function getVoicedrivenBaseAssetVolume(sceneGroup: SceneGroup): number {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        return sceneGroup.mediaAsset.volume;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        return sceneGroup.voiceoverAsset.volume;
    }
    return 0;
}

export function isVoiceDrivenSceneUsingPlaceholderBaseAsset(sceneGroup: Readonly<SceneGroup>) {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        const sourceData = sceneGroup.mediaAsset.sourceData;
        return (
            sourceData &&
            sourceData.preview_url === TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA.preview_url
        );
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        const source = sceneGroup.voiceoverAsset.sourceData;
        return (
            isAudioSourceData(source) &&
            source.mp3_url === VOICEOVER_PLACEHOLDER_ASSET_SOURCE.mp3_url
        );
    }
    return false;
}

export function isScreenRecordingSceneUsingPlaceholderBaseAsset(scene: Scene) {
    const mediaAssets = findScreenRecordingMediaAssetsInScene(scene);
    if (mediaAssets.length === 0) {
        return false;
    }
    return mediaAssets.every((mediaAsset) => {
        const sourceData = mediaAsset.sourceData;
        return (
            sourceData &&
            sourceData.preview_url === TALKING_HEAD_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA.preview_url
        );
    });
}

export function isVoiceDrivenBaseAssetLoaded(sceneGroup: SceneGroup) {
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        return sceneGroup.mediaAsset.isLoaded;
    } else if (isVoiceoverSceneGroup(sceneGroup)) {
        return sceneGroup.voiceoverAsset.isLoaded;
    }
    return false;
}

/**
 * A function that returns whether a voice driven scene group's base asset can be overwritten with a new asset.
 * Talking head scenes should not update under the following conditions:
 *    1. If new asset is not valid (not from the correct category)
 *    2. If it has overlays
 *    3. If the captions have been updated/edited by the user
 * @param sceneGroup - the scene group to check
 * @param isOriginalTranscript - whether the transcript is original or missing (use getIsOriginalOrMissingTranscript selector)
 * @param sourceData - optionally provide the new sourceData, if we are considering a specific new asset
 * @returns an object in the form: { result: boolean, reason?: string }
 */
export function canVoiceDrivenBaseAssetBeOverwritten(
    sceneGroup: Readonly<SceneGroup>,
    isOriginalTranscript: boolean,
    sourceData?: FlattenedAsset | AudioAssetModel,
): { result: boolean; title?: string; reason?: string } {
    if (!isVoiceDrivenSceneGroup(sceneGroup)) {
        throw Error(
            'canVoiceDrivenBaseAssetBeOverwritten should only be called for voice driven scene groups',
        );
    }

    if (sourceData) {
        // if we are considering a specific new asset, check if it is valid. This is different for talking head, ai avatar and voiceover scenes
        if (isAudioAsset(sourceData)) {
            const isSourceDataVoiceoverCompatible =
                sourceData.source === SourceEnum.SOURCE_USER_VOICEOVER ||
                sourceData.source === SourceEnum.SOURCE_AI_VOICEOVER;
            if (!isSourceDataVoiceoverCompatible) {
                return {
                    result: false,
                    reason: 'Cannot apply voiceover: please use a clip from the voiceover tab.',
                };
            }
            if (!isVoiceoverSceneGroup(sceneGroup)) {
                return {
                    result: false,
                    reason: 'Cannot apply voiceover: please switch to a scene created for voiceover media.',
                };
            }
        } else {
            const mediaSourceData = sourceData;
            if (
                mediaSourceData.source === SourceEnum.SOURCE_USER_STICKERS ||
                mediaSourceData.source === SourceEnum.SOURCE_TENOR
            ) {
                return {
                    result: false,
                    reason: 'Cannot apply media: please create a new scene.',
                };
            }
            const isInvalidMedia =
                mediaSourceData.source !== SourceEnum.SOURCE_USER ||
                mediaSourceData.type === ('image' as SlideBackgroundModelTypeEnum);
            if (isInvalidMedia && isTalkingHeadSceneGroup(sceneGroup)) {
                return {
                    result: false,
                    title: 'This media does not contain any spoken words',
                    reason: 'Talking head scenes require audio to generate a transcript. To use this media, please create a new scene.',
                };
            }
            if (isAIAvatarSceneGroup(sceneGroup)) {
                return {
                    result: false,
                    reason: 'Cannot apply media: please switch to a non-AI avatar scene.',
                };
            }
            if (!isTalkingHeadSceneGroup(sceneGroup)) {
                return {
                    result: false,
                    reason: 'Cannot apply media: please switch to a scene created for talking head media.',
                };
            }
            if (
                isVoiceDrivenSceneUsingPlaceholderBaseAsset(sceneGroup) &&
                isTalkingHeadSceneGroup(sceneGroup)
            ) {
                if (flattenedAssetIsVideo(mediaSourceData)) {
                    if (!mediaSourceData.has_voiceover) {
                        return {
                            result: false,
                            reason: 'The media you selected does not appear to have any audio. Please choose another clip that contains audio of a person speaking. Otherwise, use this media create a new media scene.',
                        };
                    }
                }
            }
        }
    }

    const isPlaceholder = isVoiceDrivenSceneUsingPlaceholderBaseAsset(sceneGroup);
    const isVoiceover = isVoiceoverSceneGroup(sceneGroup);

    if (!isPlaceholder) {
        if (!isOriginalTranscript) {
            return {
                result: false,
                reason: `Your ${isVoiceover ? 'voiceover' : 'talking head'} media cannot be updated because your ${isVoiceover ? 'voiceover' : 'talking head'} transcript modifications will be lost. To use this media, create a new ${isVoiceover ? 'voiceover' : 'talking head'} scene.`,
            };
        }
        if (sceneGroup.scenes.length > 1) {
            return {
                result: false,
                reason: `Your ${isVoiceover ? 'voiceover' : 'talking head'} media cannot be updated because your scene overlays will be lost. To use this media, create a new ${isVoiceover ? 'voiceover' : 'talking head'} scene.`,
            };
        }
    }

    return {
        result: true,
    };
}

/**
 * Translates times from the Transcript into times in the SceneGroup.
 * For example, a scenegroup could use a 60s selection from a 80s clip.
 * The Transcript nodes will always have times in the range of 0-80s,
 * which will need to be translated into the scene timeline when calling seek.
 *
 * @param transcriptTime the time to "translate"
 * @param playbackRegions the playbackRegions of the base asset (see {@link getVoiceDrivenAssetPlaybackRegions})
 * @param voiceDrivenBaseAssetSourceDuration the duration of the base asset (see {@link getVoiceDrivenBaseAssetSourceDuration})
 * @returns the time in the SceneGroup's time space
 */
export function translateTranscriptTimeToSceneGroupTime(
    transcriptTime: number,
    voiceDrivenAssetPlaybackRegions: PlaybackRegions,
    voiceDrivenBaseAssetSourceDuration: number,
) {
    return VideoData.assetTimeToPlaybackTime(
        voiceDrivenAssetPlaybackRegions,
        voiceDrivenBaseAssetSourceDuration,
        transcriptTime,
    );
}

export function translateTranscriptTimeToVideoTime(
    transcriptTime: number,
    voiceDrivenAssetPlaybackRegions: PlaybackRegions,
    voiceDrivenBaseAssetSourceDuration: number,
    sceneGroupStartTime: number,
) {
    return translateSceneGroupTimeToVideoTime(
        translateTranscriptTimeToSceneGroupTime(
            transcriptTime,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenBaseAssetSourceDuration,
        ),
        sceneGroupStartTime,
    );
}
/**
 * The opposite of translateTranscriptTimeToSceneGroupTime.
 * @param sceneGroupTime the local time within the sceneGroup
 * @param voiceDrivenAssetPlaybackRegions the playbackRegions of the base asset (see {@link getVoiceDrivenAssetPlaybackRegions})
 * @param voiceDrivenBaseAssetSourceDuration the duration of the base asset (see {@link getVoiceDrivenBaseAssetSourceDuration})
 * @param opts.includeTrailingCut by default, if the sceneGroupTime is right at a cut, this function will return the startTime of the subsequent playbackRegion.
 *                                Sometimes it is useful to instead get the endTime of the current playbackRegion. In that case, set this to false.
 *                                The most common use-case for includeTrailingCut=false is when you want to convert the endTime of an overlay. Example:
 *                cutStart                 cutEnd
 * <-----------------|------------------------|----------------------> transcript time
 * <---[---OVERLAY---]---------------->
 *
 * In this case, the overlay end time is simultaneously both the cutStart AND cutEnd time (because the cut
 * is removed from transcript).
 * But we want overlays to not greedily consume the neighbouring cut in the transcript, so we can call:
 *
 * overlayEndTime = translateSceneGroupTimeToTranscriptTime(... ,{ includeTrailingCut: false });
 *
 * To specify that we want cutStart, not cutEnd.
 *
 * If an overlay starts at a cut end, this same ambiguous situation arises. But in that case, we want cutEnd time, so
 * includeTrailingCut should be true.
 *
 * @returns transcript time
 */
export function translateSceneGroupTimeToTranscriptTime(
    sceneGroupTime: number,
    voiceDrivenAssetPlaybackRegions: PlaybackRegions,
    voiceDrivenBaseAssetSourceDuration: number,
    { includeTrailingCut = true } = {},
) {
    if (!includeTrailingCut) {
        let playbackDuration = 0;
        for (let i = 0; i < voiceDrivenAssetPlaybackRegions.length; i++) {
            const [start, end] = voiceDrivenAssetPlaybackRegions[i];
            playbackDuration = playbackDuration + (end - start);
            if (equalWithinEpsilon(playbackDuration, sceneGroupTime)) {
                return end;
            }
        }
    }
    return VideoData.playbackTimeToAssetTime(
        voiceDrivenAssetPlaybackRegions,
        voiceDrivenBaseAssetSourceDuration,
        sceneGroupTime,
    );
}

export function translateVideoTimeToSceneGroupTime(videoTime: number, sceneGroupStartTime: number) {
    return videoTime - sceneGroupStartTime;
}

export function translateSceneGroupTimeToVideoTime(
    sceneGroupTime: number,
    sceneGroupStartTime: number,
) {
    return sceneGroupTime + sceneGroupStartTime;
}

/**
 * For an explanation of includeTrailingCut, see {@link translateSceneGroupTimeToTranscriptTime}
 */
export function translateVideoTimeToTranscriptTime(
    videoTime: number,
    voiceDrivenAssetPlaybackRegions: PlaybackRegions,
    voiceDrivenBaseAssetSourceDuration: number,
    sceneGroupStartTime: number,
    { includeTrailingCut = true } = {},
) {
    return translateSceneGroupTimeToTranscriptTime(
        translateVideoTimeToSceneGroupTime(videoTime, sceneGroupStartTime),
        voiceDrivenAssetPlaybackRegions,
        voiceDrivenBaseAssetSourceDuration,
        {
            includeTrailingCut,
        },
    );
}

export function sceneContainsIcon(scene: Scene): boolean {
    return scene.getAssetsByType(VideoData.ICON_ASSET_TYPE).length > 0;
}

export function sceneContainsEditableMedia(scene: Scene): boolean {
    return (
        scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE).filter((asset) => asset.isEditable)
            .length > 0
    );
}

/**
 * For our analytics it's useful to know what media asset was recommended
 */
export function recommendedMediaSourceData(recommendedSceneData: GenerateSceneMetadata): {
    type: 'icon' | 'image' | 'video' | 'none';
    sourceData: MediaSourceData | IconSourceData | null;
} {
    // check for icons first (prioritize them)
    const iconAsset = recommendedSceneData.assets.find(
        (asset) => asset.type === VideoData.ICON_ASSET_TYPE,
    );
    if (iconAsset) {
        return {
            type: 'icon',
            sourceData: (iconAsset as GenerateIconAssetMetadata).sourceData as IconSourceData,
        };
    }

    // check for media assets
    const mediaAsset = recommendedSceneData.assets.find(
        (asset) => asset.type === VideoData.MEDIA_ASSET_TYPE,
    );
    if (mediaAsset) {
        const asset = mediaAsset as GenerateMediaAssetMetadata;
        return {
            type: (asset.sourceData as MediaSourceData).type === 'image' ? 'image' : 'video',
            sourceData: asset.sourceData as MediaSourceData,
        };
    }

    return {
        type: 'none',
        sourceData: null,
    };
}

export function isSceneGroupOutroOrBrand(sceneGroup: SceneGroup) {
    return sceneGroup.scenes.some(
        (scene) =>
            scene.sceneCategory === VideoData.CATEGORY_BRAND ||
            scene.sceneCategory === VideoData.CATEGORY_OUTRO,
    );
}

function getIsPrePackagedMorphWithNextScene(scene: Scene | undefined): boolean {
    return !!scene?.isPrePackagedMorphWithNextScene;
}

export type PrePackagedMorphPosition = 'start' | 'middle' | 'end' | null;

export function isSecondarySceneSequencePosition(position: PrePackagedMorphPosition): boolean {
    return position === 'middle' || position === 'end';
}

export function getPrePackagedMorphPosition(scene: Scene): PrePackagedMorphPosition {
    const prevChained = getIsPrePackagedMorphWithNextScene(scene.prevScene);
    const selfChained = getIsPrePackagedMorphWithNextScene(scene);

    if (!prevChained && selfChained) return 'start';
    if (prevChained && selfChained) return 'middle';
    if (prevChained && !selfChained) return 'end';
    return null;
}

/** Returns true if inserting a scene group at `targetIndex` would split a pre-packaged scene sequence */
export function wouldSplitPrePackagedSceneSequence(
    luminary: Luminary,
    targetIndex: number,
): boolean {
    if (targetIndex <= 0 || targetIndex >= luminary.sceneGroups.length) return false;
    const prevGroup = luminary.sceneGroups[targetIndex - 1];
    const prevLastScene = prevGroup.scenes.at(-1);
    return getIsPrePackagedMorphWithNextScene(prevLastScene);
}

/**
 * Given a scene group, returns all scene group IDs in its pre-packaged scene sequence.
 * If the scene group is not a chain start, returns just its own ID.
 */
export function getPrePackagedSceneSequenceSceneGroupIds(
    luminary: Luminary,
    sceneGroupId: string,
): string[] {
    const startGroup = luminary.getSceneGroupById(sceneGroupId);
    if (!startGroup) return [sceneGroupId];

    const firstScene = startGroup.scenes[0];
    if (!firstScene || getPrePackagedMorphPosition(firstScene) !== 'start') {
        return [sceneGroupId];
    }

    const ids = [sceneGroupId];
    let currentIndex = startGroup.index;

    while (currentIndex < luminary.sceneGroups.length - 1) {
        const currentGroup = luminary.sceneGroups[currentIndex];
        const lastScene = currentGroup.scenes.at(-1);
        if (!getIsPrePackagedMorphWithNextScene(lastScene)) break;
        currentIndex++;
        ids.push(luminary.sceneGroups[currentIndex].id);
    }

    return ids;
}

export function getPrePackagedSceneSequenceSceneIds(scene: Scene): string[] {
    if (getPrePackagedMorphPosition(scene) !== 'start') return [scene.id];
    const ids = [scene.id];
    const walkChain = (current: Scene): void => {
        if (!current.nextScene || !getIsPrePackagedMorphWithNextScene(current)) return;
        ids.push(current.nextScene.id);
        walkChain(current.nextScene);
    };
    walkChain(scene);
    return ids;
}

type ChainDurationEntry = { slideId: string; duration: number };

/**
 * For a chain-start scene group, returns the combined duration and per-member breakdown.
 * Returns null if not a chain start or chain has only one member.
 */
export function getPrePackagedSceneSequenceDurationInfo(
    luminary: Luminary,
    sceneGroup: SceneGroup,
): { entries: ChainDurationEntry[]; combinedDuration: number } | null {
    const firstScene = sceneGroup.scenes[0];
    if (!firstScene) return null;
    const position = getPrePackagedMorphPosition(firstScene);
    if (position !== 'start') return null;

    const chainIds = getPrePackagedSceneSequenceSceneGroupIds(luminary, sceneGroup.id);
    if (chainIds.length <= 1) return null;

    const entries: ChainDurationEntry[] = [];
    let combinedDuration = 0;
    for (const id of chainIds) {
        const group = luminary.getSceneGroupById(id);
        entries.push({ slideId: group.scenes[0].id, duration: group.duration });
        combinedDuration += group.duration;
    }

    return { entries, combinedDuration };
}

export function getSceneNumber(state: RootState, scene: Scene): string | number | null {
    if (isSceneGroupOutroOrBrand(scene.sceneGroup)) {
        // outro and branding scenes don't have scene numbers
        return null;
    }
    const luminary: Luminary = scene.root;
    let slideNumber = 1;
    let chainSuffixIndex = 0;
    const scenes = luminary.scenes;
    for (const thisScene of scenes) {
        const thisSceneGroup = thisScene.sceneGroup;
        if (isVoiceDrivenSceneGroup(thisSceneGroup) && !isSceneAnOverlay(state, thisScene)) {
            if (thisScene.id === scene.id) {
                return null;
            }
            continue;
        }

        const prevChained = getIsPrePackagedMorphWithNextScene(thisScene.prevScene);
        const selfChained = getIsPrePackagedMorphWithNextScene(thisScene);

        if (!prevChained && selfChained) {
            chainSuffixIndex = 0;
        }

        if (thisScene.id === scene.id) {
            const inChain = prevChained || selfChained;
            return inChain
                ? `${slideNumber}${String.fromCharCode(97 + chainSuffixIndex)}`
                : slideNumber;
        }

        if (selfChained) {
            chainSuffixIndex++;
            continue;
        }

        slideNumber++;
    }
    return null;
}

export const hasVisibleCaptionAsset = (luminary: Readonly<Luminary>) => {
    return luminary.sceneGroups.some((sceneGroup) => {
        return isVoiceDrivenSceneGroup(sceneGroup) && sceneGroup.captions.visible;
    });
};

export const firstCaptionAsset = (luminary: Readonly<Luminary>) => {
    const sceneGroup = getFirstVoiceDrivenSceneGroup(luminary);
    return sceneGroup?.captions?.asset;
};

export function getCaptionType(
    luminary: Readonly<Luminary>,
): 'fancy caption' | 'text asset' | 'none' {
    const captionAsset = firstCaptionAsset(luminary);
    if (!captionAsset) {
        return 'none';
    }
    return VideoData.isFancyCaptionAsset(captionAsset) ? 'fancy caption' : 'text asset';
}

/**
 * Returns true if the scene contains a fancy caption asset
 */
export function hasFancyCaptions(scene: Scene) {
    return scene.getAssetsByType(VideoData.FANCY_CAPTION_ASSET_TYPE).length > 0;
}

export function isBrandSceneGroup(sceneGroup: SceneGroup) {
    const category = getSceneGroupCategory(sceneGroup);
    return category === VideoData.CATEGORY_BRAND;
}

/**
 * Checks if Luminary is empty.
 * We consider Luminary to be empty if there are no scenes or if there is just the branding scene.
 */
export function isLuminaryEmpty(luminary: Readonly<Luminary>): boolean {
    return (
        luminary.scenes.length === 0 || (luminary.scenes.length === 1 && luminary.hasBrandingScene)
    );
}

export function doesLuminaryContainAIAvatar(luminary: Readonly<Luminary>): boolean {
    return luminary.sceneGroups.some(isAIAvatarSceneGroup);
}

export function getVideoMusicVolume(luminary: Readonly<Luminary>): number {
    if (!luminary.musicAsset) {
        return AudioVolume.MAX;
    }
    return luminary.musicAsset.volume;
}

export function getSfxVolume(luminary: Readonly<Luminary>): number {
    return luminary.sfxVolume;
}

export function getNumNonOutroNonBrandSceneGroups(luminary: Readonly<Luminary>) {
    return luminary.sceneGroups.filter((sceneGroup) => {
        const category = getSceneGroupCategory(sceneGroup);
        return category !== VideoData.CATEGORY_OUTRO && category !== VideoData.CATEGORY_BRAND;
    }).length;
}

export function getAdvancedMediaControlsAsset(
    luminary: Readonly<Luminary>,
    sceneId?: string,
    assetId?: string,
) {
    const scene = sceneId ? luminary.getSceneById(sceneId) : undefined;
    const asset = scene && assetId ? scene.getAssetById(assetId) : undefined;
    let mediaAsset: TimeAwareMediaAsset | undefined;
    let mediaContainer: Scene | SceneGroup | undefined = scene;

    if (asset && VideoData.isMediaAsset(asset)) {
        mediaAsset = asset;
    }

    if (scene && mediaAsset && mediaAsset.linkToSceneGroup) {
        // For linked assets, we want to edit the base media asset on the scene group
        mediaAsset = scene.sceneGroup.mediaAsset;
        mediaContainer = scene.sceneGroup;
    }
    return {
        mediaAsset,
        mediaContainer,
    };
}

// Returns the current template colors codes in array format
export function getTemplateColorCodes(luminary: Readonly<Luminary>): number[] {
    const templateColors = luminary.template.colors;
    return templateColors.map((templateColor) => templateColor.color);
}

/**
 * Gets the background color from the first scene in a scene group that has a background color asset.
 * Returns null if no scene has a background color.
 */
export function getSceneGroupBackgroundColor(sceneGroup: SceneGroup): number | null {
    for (const scene of sceneGroup.scenes) {
        const bgAssetProperties = getBackgroundColorAssetProperties(scene);
        if (bgAssetProperties) {
            return bgAssetProperties.color;
        }
    }
    return null;
}

// Checks if MediaAsset in scene is zoomed out below 100%
export function isMediaZoomedOut(scene: Scene): boolean {
    const mediaAssets = scene.getAssetsByType(VideoData.MEDIA_ASSET_TYPE);
    if (!mediaAssets?.length) {
        return false;
    }

    return mediaAssets[0].scaleX < 1.0 && mediaAssets[0].scaleY < 1.0;
}

// Checks if scene sized LottieAsset is below TimeBasedMediaAsset in scene
// this is a heuristic to dermine if the LottieAsset is blocking the background
// under the assumption that a LottieAsset above the TimeBasedMdiaAsset then
// it likely has a transparent section
// while a LottieAsset below likely coveres anything below it (including bottom ColorAsset i.e. 'background color')
export function isLottieBackground(scene: Scene): boolean {
    const lottieIndexes = scene
        .getAssets()
        .map((asset, index) => (asset.classType === VideoData.LOTTIE_ASSET_TYPE ? index : null))
        .filter((index) => index !== null);

    if (lottieIndexes.length < 1) {
        return false;
    }

    const mediaAssetIndexes = scene
        .getAssets()
        .map((asset, index) => (asset.classType === VideoData.MEDIA_ASSET_TYPE ? index : null))
        .filter((index) => index !== null);

    if (mediaAssetIndexes.length < 1) {
        return true;
    }

    return Math.max(...lottieIndexes) < Math.max(...mediaAssetIndexes);
}

// Checks if the final asset is a ColorAsset -- which means it is the "background"
export function isBottomAssetColorAsset(scene: Scene): boolean {
    const bottomAsset = scene.getAssets()[0];
    if (!bottomAsset) {
        return false;
    }
    return VideoData.isColorAsset(bottomAsset);
}

// Provides the ColorAsset properites for the ColorAsset at bottom of the scene--the 'background color'
// provided in a format used by CustomToolbarColorPicker
export function getBackgroundColorAssetProperties(
    scene: Scene,
): { assetId: string; path: string; color: number; index: number } | null {
    const bottomAsset = scene.getAssets()[0];
    if (!bottomAsset) {
        return null;
    }

    if (!VideoData.isColorAsset(bottomAsset)) {
        return null;
    }

    return {
        assetId: bottomAsset.id,
        path: 'sourceColor',
        index: 0,
        color: bottomAsset.sourceColor,
    };
}

export function assetHasEntryAnimation(asset: Asset): boolean {
    const animations = getAllAssetAnimations(asset);
    for (let i = 0; i < animations.length; i++) {
        const animation = animations[i];
        if (animation.property === 'alpha') {
            const startValue = animation.startValue;
            const endValue = animation.endValue;
            if (typeof startValue !== 'number' || typeof endValue !== 'number') {
                continue;
            }
            if (startValue === 0 && endValue > 0) {
                return true;
            }
        }
    }
    return false;
}

export function getAllAssetAnimations(asset: Asset) {
    if (
        VideoData.isMediaAsset(asset) ||
        VideoData.isIconAsset(asset) ||
        VideoData.isAssetGroup(asset)
    ) {
        return asset.objectAnimation;
    }
    if (VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset)) {
        return asset.normalStyle.animationConfigurations;
    }
    return [];
}

export function isEligibleForMultipleSpeakers(asset: TimeAwareMediaAsset): boolean {
    return asset.isEditable && !VideoData.isStickerAsset(asset);
}

export function isAnyTextAsset(
    luminary: Luminary | Readonly<Luminary>,
    assetId: string | null,
): boolean {
    if (!assetId) {
        return false;
    }
    const asset = luminary.getAssetById(assetId);
    if (!asset) {
        return false;
    }
    return VideoData.isTextAsset(asset) || VideoData.isFancyCaptionAsset(asset);
}

export function getAssetSfxName(
    luminary: Readonly<Luminary>,
    sceneId: string,
    assetId: string,
): string | null {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) return null;
    const asset = scene.getAssetById(assetId);
    if (!asset) return null;
    if (asset.sfx.length === 0) return null;
    return asset.sfx[0].sourceData?.name ?? null;
}

export function getAssetSfxUuid(
    luminary: Readonly<Luminary>,
    sceneId: string,
    assetId: string,
): string | null {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) return null;
    const asset = scene.getAssetById(assetId);
    if (!asset) return null;
    if (asset.sfx.length === 0) return null;
    return asset.sfx[0].sourceData?.uuid ?? null;
}

export function getSceneSfxName(luminary: Readonly<Luminary>, sceneId: string): string | null {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) return null;
    const sfx = scene.sfx || [];
    if (sfx.length === 0) return null;
    return sfx[0].sourceData?.name ?? null;
}

export function getSceneSfxUuid(luminary: Readonly<Luminary>, sceneId: string): string | null {
    const scene = luminary.getSceneById(sceneId);
    if (!scene) return null;
    const sfx = scene.sfx || [];
    if (sfx.length === 0) return null;
    return sfx[0].sourceData?.uuid ?? null;
}
